import { readAtom } from "@mdotm/shadcn/legacy/reactivity";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import { useEffect, useRef } from "react";
import { PortfolioLabContext } from "../ctx";
import { useInvestmentLab } from "./useInvestmentLab";

/**
 * Recomputes, once when the lab finishes loading, the restored columns whose stored reports were
 * computed from an earlier composition revision than the server's.
 *
 * A composition edit autosaves through `PUT /virtual-investments/{uuid}`, a pure persist, while the
 * metrics, the exposure breakdown and the NAV series all read `entity.reports`, which only
 * `POST /compute` writes. A user who edits weights and leaves without clicking "Update Charts" leaves
 * the server holding a newer revision than its reports; the list response says so
 * (`reportsComputedAtRevision !== compositionRevision`), and this pass computes exactly those columns.
 * A column whose revisions match is not recomputed, even if its reports are on older market data:
 * "Update Charts" is where a recompute of fresh columns is the user's to ask for.
 *
 * The compute skips the composition push (`skipCompositionPush`) because the client state was seeded
 * FROM the server — there is nothing new to send, and sending it would rewrite baseline state on a
 * plain page load.
 *
 * Fire-and-forget: `reloadInvestmentsMetrics` reports its own failures and drives `metricsStatus$`.
 * The compute runs off the column's write lane, so a user edit made mid-pass saves without waiting
 * for it, and the compute's result is then discarded as superseded.
 */
export function useRecomputeMetricsOnLoad(): void {
	const ctx = useMandatoryContext(PortfolioLabContext);
	const { reloadInvestmentsMetrics } = useInvestmentLab();
	// One pass per lab mount. Re-renders are already handled by the dependency list: both deps are
	// stable — `ctx.investments$` is the atom itself (never reallocated; edits write THROUGH it) and
	// `reloadInvestmentsMetrics` is a `useCallback` keyed on the context value. What the deps cannot
	// cover is StrictMode's dev-only mount → unmount → remount, which re-runs the effect on the same
	// component instance; this ref survives that, so the lab does not fire a doubled compute per
	// column in development. Removing it costs nothing in production and a wasted compute pass in dev.
	const hasRun = useRef(false);

	useEffect(() => {
		if (hasRun.current) {
			return;
		}
		hasRun.current = true;
		const investments = readAtom(ctx.investments$);
		if (investments.length === 0) {
			return;
		}
		void reloadInvestmentsMetrics(investments, { skipCompositionPush: true, onlyStale: true });
	}, [ctx.investments$, reloadInvestmentsMetrics]);
}
